import { useCallback, useEffect, useMemo, useState } from 'react'
import MainLayout from '../../../layouts/MainLayout'
import { usePermissions } from '../../../hooks/usePermissions'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  faTrash,
  faEye,
  faShippingFast,
  faCircleChevronRight,
  faCircleChevronLeft,
  faCircleExclamation,
  faPrint,
  faFileSignature,
  faCheck,
} from '@fortawesome/free-solid-svg-icons'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import useBaseService from '../../../hooks/services/useBaseService'
import { entities } from '../../../constants/entities'
import useAction from '../../../hooks/useAction'
import ListHeader from '../../../components/ListHeader'
import PageHeader from '../../../components/PageHeader'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import useUrlFilters from '../../../hooks/useUrlFilters'
import useFilterHandlers from '../../../hooks/useFilterHandlers'
import useGenericFilter from '../../../hooks/filters/useGenericFilter'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import AutoCompleteFilter from '../../../components/AutoCompleteFilter'
import baseService from '../../../services/baseService'
import AlertMessage from '../../../components/AlertMessage'
import FilterCollapse from '../../../components/FilterCollapse'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { shipmentStatusColorMap } from '../../../utils/statusColorMap'
import {
  ShipmentStatusEnum,
  ShipmentStatusEnumById,
} from '../../../enums/shipment-status-enum'
import { formatDate, safe } from '../../../utils/objectUtils'
import { invalidateByEvent } from '../../../utils/query-invalidator'
import { GenericStatusEnum } from '../../../enums/generic-status-enum'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ShipmentItemStatus,
  ShipmentItemStatusEnumById,
} from '../../../enums/shipmentItemStatus'
import { Box, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemText } from '@mui/material'
import api from '../../../services/api'
import { ExportProtocolFinal } from './components/ExportProtocolFinal'
import { ManageProtocolsModal } from './components/ManageProtocolsModal'

const ShipmentsPage = () => {
  const { canAccess } = usePermissions()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { showLoader, hideLoader } = useLoader()
  const { showNotification } = useNotification()
  const {
    get: baseServiceGetAll,
    get: baseServiceGet,
    del: baseServiceDelete,
  } = useBaseService(navigate)

  const queryParams = new URLSearchParams(location.search)

  const movementId = queryParams.get('movement_id')

  const [openExportProtocol, setOpenExportProtocol] = useState(false)
  const [invoiceChoices, setInvoiceChoices] = useState([])
  const [invoiceShipmentId, setInvoiceShipmentId] = useState(null)

  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
    setSelectedItem,
  } = useAction(navigate)

  // --- Padronização de filtros via useUrlFilters ---
  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      id: [],
      'movements.recipient_customer.alias': [],
      'movements.destination.alias': [],
      'status.name': [],
      deleted_at: false,
    },
    'shipments'
  )

  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters)

  // --- Gerenciamento de Filtros Locais do Formulário ---
  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        id: [],
        'movements.recipient_customer.alias': [],
        'movements.destination.alias': [],
        'status.name': [],
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear
    )

  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message)
      navigate(location.pathname, { replace: true })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  // Busca todos os movimentos
  const {
    data: movementsData,
    isLoading: isLoadingMovementsData,
    isFetching: isFetchingMovementsData,
  } = useQueryWithLoader({
    queryKey: ['movements'],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl({
        deleted_at: false,
        maintenance_created_movement: false,
        status_id: [
          GenericStatusEnum.NOT_ATTENDED,
          GenericStatusEnum.PARTIALLY_ATTENDED,
        ], // NÃO ATENDIDO | PARCIALMENTE ATENDIDO
      })
      const params = { filters: filtersTransformed, page: 1, perPage: 0 }
      const response = await baseServiceGet(entities.movements.get, params)

      return response.result.data
    },
    keepPreviousData: true,
    refetchOnWindowFocus: true,
    select: (data) => data.map((item) => ({ id: item.id, label: item.id })),
  })

  // Busca todos os carregamentos
  const {
    data: shipmentsData,
    isLoading: isLoadingShipments,
    isFetching: isFetchingShipments,
  } = useQueryWithLoader({
    queryKey: ['shipments', filters, pagination],
    queryFn: async () => {
      showLoader()
      const filtersTransformed = transformFiltersForUrl({
        ...filters,
        ...(movementId && { 'movements.movement_id': movementId }),
      })
      const params = {
        filters: filtersTransformed,
        page: pagination.page,
        perPage: pagination.perPage,
        sort: {
          column: 'id',
          order: 'desc',
        },
      }
      const response = await baseServiceGetAll(entities.shipments.get, params)
      return response.result.data
    },
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    keepPreviousData: true,
    select: (data) => {
      const getCurrentMovement = (item, movementId) => {
        if (!item.movements?.length) return null

        return (
          item.movements.find((m) => m.id === movementId) || item.movements[0]
        )
      }

      const getDeliveredDestination = (shipment, movement) => {
        const shipmentItems = [
          ...(Array.isArray(shipment?.shipment_items)
            ? shipment.shipment_items
            : []),
          ...(Array.isArray(shipment?.items) ? shipment.items : []),
        ]
        const addresses = [
          ...new Set(
            shipmentItems
              .filter(
                (shipmentItem) =>
                  shipmentItem?.status_id === ShipmentItemStatus.ENTREGUE &&
                  shipmentItem?.movement_item?.movement_id != null &&
                  String(shipmentItem?.movement_item?.movement_id) ===
                    String(movement?.id)
              )
              .map(
                (shipmentItem) =>
                  shipmentItem?.address?.alias ||
                  shipmentItem?.address?.formatted_address
              )
              .filter(Boolean)
          ),
        ]

        return addresses.length
          ? addresses.join(', ')
          : safe(movement?.destination?.alias)
      }

      const shipments = data.map((item) => {
        const base = {
          id: safe(item.id),
          status: safe(item.status?.name),
          status_id: safe(item.status?.id),
          delivery_date: formatDate(item.updated_at),
          created_at: formatDate(item.created_at),
          has_invoice: !!item.has_invoice,
          has_signed_protocol: !!item.has_signed_protocol,
          requires_invoice: !!item.requires_invoice,
          isDeleted: !!item.deleted_at,
        }

        if (movementId) {
          const movement = getCurrentMovement(item, movementId)

          return {
            ...base,
            movement_id: safe(movement?.id),
            movement_type_id: safe(movement.movement_type?.id),
            recipient_customer_id: safe(movement?.recipient_customer?.id),
            recipient_customer: safe(movement?.recipient_customer?.alias),
            destination: getDeliveredDestination(item, movement),
            organization: safe(movement?.organization?.alias),
            created_by: safe(item.user?.name),
          }
        }

        return {
          ...base,
          status: safe(ShipmentStatusEnumById[item.status?.id]),
          movements:
            item.movements?.map((movement) => ({
              movement_id: safe(movement.id),
              movement_number: safe(movement.number),
              movement_type_id: safe(movement.movement_type?.id),
              movement_type: safe(movement?.movement_type?.name),
              recipient_customer_id: safe(movement?.recipient_customer?.id),
              recipient_customer: safe(movement?.recipient_customer?.alias),
              average_shipment_status:
                ShipmentItemStatusEnumById[movement?.average_shipment_status],
              average_shipment_status_id: movement?.average_shipment_status,
              destination_order: getDeliveredDestination(item, movement),
            })) || [],
        }
      })

      return {
        shipments,
        totalPages: data.last_page,
      }
    },
  })

  const { shipments = [], totalPages = 1 } = shipmentsData || {}

  const deleteShipmentMutation = useMutation({
    mutationFn: async (shipment) => {
      showLoader()
      const response = await baseServiceDelete(
        entities.shipments.delete(shipment.id)
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        // Invalida ShipmentsPage
        invalidateByEvent(queryClient, 'SHIPMENT_DELETE')
      }
    },
    onError: (error) => {
      console.error('onError', error)
      hideLoader()
    },
  })

  const openShipmentInvoices = useCallback(async (shipment) => {
    try {
      const result = await baseServiceGet(entities.shipments.romaneio.get(shipment.id))
      const romaneios = result?.result || result?.romaneio || []
      const choices = (Array.isArray(romaneios) ? romaneios : [romaneios]).flatMap((romaneio) =>
        (romaneio?.documents || []).filter((document) => {
          const type = String(document.document_type || document.type || '').toUpperCase()
          return type === 'NOTA_FISCAL' && !document.deleted_at && document.active !== false
        }).map((document) => ({ romaneio, document }))
      )
      setInvoiceShipmentId(shipment.id)
      setInvoiceChoices(choices)
      if (!choices.length) showNotification('warning', 'Nenhuma Nota Fiscal ativa foi encontrada.')
    } catch (error) {
      showNotification('error', error?.response?.data?.message || 'Não foi possível consultar as Notas Fiscais.')
    }
  }, [baseServiceGet, showNotification])

  const downloadGroupInvoice = async ({ romaneio, document }) => {
    const anchor = window.document.createElement('a')
    anchor.style.display = 'none'
    window.document.body.appendChild(anchor)
    try {
      const response = await api.get(entities.shipments.romaneio.downloadDocument(invoiceShipmentId, romaneio.id, document.id), { responseType: 'blob' })
      const blob = response?.data || response
      const url = URL.createObjectURL(blob)
      anchor.href = url
      anchor.download = document.original_file_name || `nota-fiscal-${romaneio.customer_name || romaneio.id}`
      anchor.click()
      window.setTimeout(() => URL.revokeObjectURL(url), 60000)
      setInvoiceChoices([])
    } catch (error) {
      showNotification('error', error?.response?.data?.message || 'Não foi possível baixar esta Nota Fiscal.')
    } finally {
      anchor.remove()
    }
  }

  // Consolida o estado de carregamento para o loader global da tabela
  const isPageLoading = isLoadingShipments || deleteShipmentMutation.isPending

  // Efeito para mostrar/esconder o loader global
  useEffect(() => {
    if (isPageLoading) {
      showLoader()
    } else {
      hideLoader()
    }
  }, [isPageLoading, showLoader, hideLoader])

  // --- Colunas e Ações da tabela ---
  const columns = [
    { key: 'id', label: 'Número' },
    ...(movementId
      ? [
          { key: 'recipient_customer', label: 'Cliente Destinatário' },
          { key: 'destination', label: 'Destino' },
        ]
      : []),
    { key: 'status', label: 'Status' },
    { key: 'delivery_date', label: 'Confirmado Em' },
    { key: 'created_at', label: 'Criado Em' },
    // * Necessita de nota fiscal
    {
      key: 'has_invoice',
      label: 'NF Anexada',
      render: (value) => {
        return (
          <Chip
            label={value ? 'Sim' : 'Não'}
            size="small"
            color={value ? 'primary' : 'secondary'}
          />
        )
      },
    },
    {
      key: 'requires_invoice',
      label: 'Necessita NF',
      render: (value) => {
        return (
          <Chip
            label={value ? 'Sim' : 'Não'}
            size="small"
            color={value ? 'success' : 'warning'}
          />
        )
      },
    },
  ]

  const collapseConfig = {
    key: ['movements'],
    columns: [
      [
        { key: 'movements.movement_number', label: 'Nº Protocolo' },
        { key: 'movements.movement_type', label: 'Tipo de Movimento' },
        { key: 'movements.recipient_customer', label: 'Cliente Destinatário' },
        { key: 'movements.destination_order', label: 'Destino' },
        {
          key: 'movements.average_shipment_status',
          label: 'Status',
          render: (value, item) => {
            const shipment_item_status_id = item?.average_shipment_status_id

            if (
              shipment_item_status_id ===
                ShipmentItemStatus.AGUARDANDO_CONFIRMACAO ||
              !shipment_item_status_id
            ) {
              return (
                <Box sx={{ color: 'warning.main' }}>
                  <FontAwesomeIcon icon={faCircleExclamation} size="xl" />
                </Box>
              )
            }

            if (shipment_item_status_id === ShipmentItemStatus.CANCELADO) {
              return (
                <Box sx={{ color: 'error.main' }}>
                  <FontAwesomeIcon icon={faCircleChevronLeft} size="xl" />
                </Box>
              )
            }

            if (shipment_item_status_id === ShipmentItemStatus.ENTREGUE) {
              return (
                <Box
                  sx={{ color: 'success.main' }}
                  title="Entregue"
                  aria-label="Entregue"
                >
                  <FontAwesomeIcon icon={faCheck} size="xl" />
                </Box>
              )
            }

            if (
              shipment_item_status_id !==
              ShipmentItemStatus.AGUARDANDO_CONFIRMACAO
            ) {
              return (
                <Box sx={{ color: 'success.main' }}>
                  <FontAwesomeIcon icon={faCircleChevronRight} size="xl" />
                </Box>
              )
            }

            // Ícone padrão ou nulo se não houver status correspondente
            return null
          },
        },
      ],
    ],
  }

  const [openUploadProtocolo, setOpenUploadProtocolo] = useState(false)

  const actions = useMemo(
    () => [
      {
        id: 'confirmShipment',
        icon: faShippingFast,
        title: 'Confirmar Carregamento',
        buttonClass: 'btn-success',
        permission: 'Confirmar carregamentos',
        onClick: (shipment) =>
          navigate(
            movementId
              ? `/aluguel/carregamentos/${shipment?.id}/confirmar?movement_type_id=${shipment?.movement_type_id}&recipient_customer_id=${shipment?.recipient_customer_id}`
              : `/aluguel/carregamentos/${shipment?.id}/confirmar`,
            {
              state: { from: location.pathname + location.search },
            }
          ),
        condition: (shipment) =>
          shipment.status_id === ShipmentStatusEnum.PENDING,
        show: (shipment) =>
          !shipment.isDeleted &&
          shipment.status_id !== ShipmentStatusEnum.DELIVERED &&
          shipment.has_signed_protocol &&
          canAccess('Confirmar carregamentos'),
      },
      {
        id: 'details',
        icon: faEye,
        title: 'Ver Detalhes',
        buttonClass: 'btn-info',
        permission: 'Ver carregamentos',
        onClick: (shipment) =>
          navigate(`/aluguel/carregamentos/${shipment.id}/detalhes/`),
      },
      {
        id: 'imprimir_nf',
        icon: faPrint,
        title: 'Imprimir Nota Fiscal',
        buttonClass: 'btn-warning',
        permission: 'Ver carregamentos',
        onClick: openShipmentInvoices,
        show: (shipment) => !shipment.isDeleted && shipment.has_invoice && canAccess('Ver carregamentos'),
      },
      {
        id: 'exportar_protocolo',
        icon: faPrint,
        title: 'Imprimir protocolo de entrega',
        buttonClass: 'btn-success',
        permission: 'Ver carregamentos',
        onClick: (shipment) => {
          setOpenExportProtocol(true)
          setSelectedItem(shipment)
        },
        show: (shipment) =>
          !shipment.isDeleted && canAccess('Ver carregamentos'),
      },
      {
        id: 'gerenciar_protocolos',
        icon: faFileSignature,
        title: 'Gerenciar Protocolos de Entrega',
        buttonClass: 'btn-info',
        permission: 'Atualizar carregamentos',
        onClick: (shipment) => {
          setSelectedItem(shipment)
          setOpenUploadProtocolo(true)
        },
        show: (shipment) =>
          !shipment.isDeleted &&
          shipment.has_invoice && // Só permite gerenciar se já tiver NF (regra de negócio)
          canAccess('Atualizar carregamentos'),
      },
      {
        id: 'delete',
        icon: faTrash,
        title: 'Excluir',
        buttonClass: 'btn-danger',
        permission: 'Excluir carregamentos',
        onClick: (shipment) =>
          showConfirmation(
            shipment,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await deleteShipmentMutation.mutateAsync(shipment)
              } catch (error) {
                console.error('Erro ao excluir carregamento:', {
                  status: error.status,
                  message: error.message,
                })
              }
            }
          ),
        condition: (shipment) => !shipment.isDeleted,
        show: (shipment) =>
          !shipment.isDeleted &&
          shipment.status_id === ShipmentStatusEnum.PENDING &&
          canAccess('Excluir carregamentos'),
      },
    ],
    [
      navigate,
      movementId,
      location.pathname,
      location.search,
      canAccess,
      setSelectedItem,
      showConfirmation,
      deleteShipmentMutation,
      openShipmentInvoices,
    ]
  )

  // Inputs para o formulário de filtros do useGenericFilter
  const inputsfilters = useMemo(
    () => [
      {
        label: 'Cliente Destinatário',
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase={['id', 'alias']}
            model="customer"
            value={localFilterValues['movements.recipient_customer.alias']}
            onChange={(selected) => {
              const val = selected?.value
              handleFilterChange(
                'movements.recipient_customer.id',
                val ? { $eq: val } : null
              )
            }}
            placeholder="Filtre pelo nome ou id do cliente"
          />
        ),
      },
      {
        label: 'Destino',
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase={['id', 'name']}
            model="destinationOrder"
            value={localFilterValues['movements.order.destination_order.name']}
            onChange={(selected) => {
              const val = selected?.value
              handleFilterChange(
                'movements.order.destination_order.id',
                val ? { $eq: val } : null
              )
            }}
            placeholder="Filtre pelo nome ou id do destino"
          />
        ),
      },
      {
        label: 'Status',
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase={['name']}
            model="shipmentStatus"
            extraFilters={{ id: { $notin: [4, 5] } }}
            value={localFilterValues['status.name']}
            onChange={(selected) => handleFilterChange('status.name', selected)}
            placeholder="Filtre pelo status"
            isMulti
            isClearable
          />
        ),
      },
    ],
    [localFilterValues, handleFilterChange]
  )

  const hasPendingMovements = movementsData?.length > 0 ?? false

  const pendingCount = movementsData?.length ?? 0

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Carregamentos"
        showBackButton={true}
        backUrl="/aluguel/movimentos"
      />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsfilters}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        {/* Cabeçalho da Lista de Carregamentos */}
        <ListHeader
          title="Lista de Carregamentos"
          buttonText="Novo Carregamento"
          buttonLink={
            movementId
              ? `/aluguel/carregamentos/validar?movement_id=${movementId}`
              : '/aluguel/carregamentos/validar'
          }
          buttonPathState={{
            from: location.pathname + location.search,
          }}
          buttonIsNotVisible={!hasPendingMovements}
          canAccess={canAccess}
          permission="Criar carregamentos"
        />

        <AlertMessage
          isLoading={isLoadingMovementsData || isFetchingMovementsData}
          condition={hasPendingMovements}
          warningMessage={
            <span>
              Existe(m){' '}
              <strong style={{ fontSize: '1rem' }}>{pendingCount}</strong>{' '}
              movimento(s) pendente(s). Clique em "Novo Carregamento" para
              continuar.
            </span>
          }
          successMessage={
            'Todos os movimentos foram atendidos. Você pode criar um novo, na seção de movimentos.'
          }
        />

        {/* Tabela Dinâmica de Carregamentos */}
        <TableWithInactiveFilter
          data={shipments}
          columns={columns}
          collapseConfig={collapseConfig}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="collapse"
          rowColorConfig={{
            colorMap: shipmentStatusColorMap,
            propertyKey: 'status',
          }}
          isLoading={isLoadingShipments}
          isFetching={isFetchingShipments}
        />

        <ExportProtocolFinal
          data={selectedItem}
          open={openExportProtocol}
          onClose={() => setOpenExportProtocol(false)}
        />

        <ManageProtocolsModal
          data={selectedItem}
          open={openUploadProtocolo}
          onClose={() => setOpenUploadProtocolo(false)}
        />

        <Dialog open={invoiceChoices.length > 0} onClose={() => setInvoiceChoices([])} fullWidth maxWidth="sm">
          <DialogTitle>Notas Fiscais do carregamento</DialogTitle>
          <DialogContent dividers>
            <List disablePadding>
              {invoiceChoices.map(({ romaneio, document }) => (
                <ListItem
                  key={`${romaneio.id}-${document.id}`}
                  secondaryAction={(
                    <Button size="small" onClick={() => downloadGroupInvoice({ romaneio, document })}>
                      Baixar NF
                    </Button>
                  )}
                >
                  <ListItemText primary={romaneio.customer_name || `Romaneio ${romaneio.id}`} secondary={document.original_file_name || 'Nota Fiscal'} />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions><Button onClick={() => setInvoiceChoices([])}>Fechar</Button></DialogActions>
        </Dialog>

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          text={action.text}
        />
      </div>
    </MainLayout>
  )
}

export default ShipmentsPage
